<template>
  <div
    v-bind="$attrs"
    v-on="$listeners"
    ref="container"
    :style="style.container"
  >
    <BeatLoaderPrimary v-if="!loadedTenantUserSkills" />
    <DataPlaceholderPrimary
      v-else-if="tenantUserSkills.length === 0"
      :style="style.placeholder"
    >
      <template v-slot:bottom>
        <template v-if="isViewingOwnPage">
          You have no skill yet.<br>
          Start adding skills now!
        </template>
        <template v-else>
          {{ firstName }} {{ lastName }} has no skills yet.<br>
          Be the first to add!
        </template>
      </template>
    </DataPlaceholderPrimary>
    <SkillGraphPrimary
      v-else
      v-bind="attrs.skillsGraph"
      :height="graphHeight"
      :isHighlighted="isHighlighted"
      :links="tenantUserSkillEdges"
      :nodes="tenantUserSkillNodes"
      :triggerFitView="triggerFitView"
      :width="graphWidth"
      @click-node="onClickNode"
    />
    <div
      :style="style.bottomContainer"
    >
      <slot name="footer">
        <div :style="style.text">
          Click skill to edit.
        </div>
        <ButtonPrimary
          @click="$emit('add-skill')"
          :style="style.button"
        >
          Add skill
        </ButtonPrimary>
      </slot>
    </div>
  </div>
</template>

<script>
  import BeatLoaderPrimary from '@modules/v2/loader/beat-loader-primary'
  import ButtonPrimary from '@modules/v2/button/button-primary'
  import DataPlaceholderPrimary from '@modules/v2/placeholder/data-placeholder-primary'
  import SkillGraphPrimary from '@modules/graph/skill-graph-primary'
  import { getTenantUserSkillEdges, skillNodeRadiusFunctions } from '@js/skill-utils'
  import { Layout } from '@js/styles'
  import { mapGetters, mapState } from 'vuex'
  import { Z_INDEXES } from '@js/constants'

  export default {
    name: 'SkillsGraph',
    inheritAttrs: false,
    components: {
      BeatLoaderPrimary,
      ButtonPrimary,
      DataPlaceholderPrimary,
      SkillGraphPrimary,
    },
    props: {
      graphHeight: {
        type: Number,
        required: false,
        default: 396,
      },
      isViewingOwnPage: {
        type: Boolean,
        required: false,
        default: false,
      },
      loadedTenantUserSkills: {
        type: Boolean,
        required: false,
        default: false,
      },
      tenantUser: {
        type: Object,
        required: false,
        default: () => {},
      },
      tenantUserSkills: {
        type: Array,
        required: false,
        default: () => [],
      },
      triggerFitView: {
        type: Boolean,
        required: false,
        efault: false,
      },
    },
    data: function () {
      return {
        graphWidth: 400,
        isHighlighted: (node) => {
          return node.isApproved === undefined || node.isApproved === false;
        },
        attrs: {
          skillsGraph: {
            /**
            * zIndex won't work without setting position to
            * something other than 'static'.
            **/
            neighborsKeyName: 'targets',
            position: 'relative',
            zIndex: Z_INDEXES.USER.SKILL_GRAPH,
          },
        },
        style: {
          bottomContainer: {
            alignItems: 'flex-end',
            bottom: 0,
            display: 'flex',
            justifyContent: 'space-between',
            padding: `0 ${Layout.space.md} ${Layout.space.md}`,
            position: 'absolute',
            width: '100%',
          },
          button: {
            zIndex: Z_INDEXES.USER.SKILL_GRAPH + 1,
          },
          container: {
            position: 'relative',
            width: '100%',
          },
          placeholder: {
            height: '100%',
          },
          text: {},
        },
      };
    },
    created: function () {
      this.style.text = {
        ...this.style.text,
        color: this.color.dark3(),
      };
    },
    mounted: function () {
      this.updateGraphWidth();
    },
    computed: {
      ...mapGetters('colorTheme', ['color']),
      ...mapState({
        windowWidth: state => state.window.width,
      }),
      firstName: function () {
        return this.tenantUser?.user?.firstName;
      },
      lastName: function () {
        return this.tenantUser?.user?.lastName;
      },
      tenantUserSkillNodes: function () {
        return this.tenantUserSkills.map(elem => {
          return {
            ...elem,
            name: elem.skill.name,
          };
        });
      },
      tenantUserSkillEdges: function () {
        return getTenantUserSkillEdges(this.tenantUserSkills);
      },
    },
    methods: {
      onClickNode: function (node) {
        const index = this.tenantUserSkills.findIndex(obj => obj.id === node.id);
        if (index >= 0) {
          this.$emit('edit-skill', this.tenantUserSkills[index]);
        }
      },
      updateGraphWidth: function () {
        this.graphWidth = this.$refs?.container?.clientWidth;
      },
    },
    watch: {
      loadedTenantUserSkills: function (newVal, oldVal) {
        this.updateGraphWidth();
      },
      windowWidth: function (newVal, oldVal) {
        this.updateGraphWidth();
      },
    },
  };
</script>
